import { apiFetch, parseErrorDetail, ApiError } from "@/api/fetch";
import { readStoredPromo } from "@/features/billing/promo";
import type {
  CheckoutResponse,
  CreateInviteCodeResponse,
  InviteCode,
  InviteRedemption,
  ManageSubscriptionResponse,
  RedeemCodeResponse,
} from "./schemas";

export { ApiError as BillingRequestError };

export const createCheckoutSession = async (): Promise<CheckoutResponse> => {
  const promo = readStoredPromo();
  const response = await apiFetch("/users/me/checkout", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(promo ? { promo } : {}),
  });
  if (!response.ok) {
    throw new ApiError(await parseErrorDetail(response), response.status);
  }
  return response.json() as Promise<CheckoutResponse>;
};

export const manageSubscription =
  async (): Promise<ManageSubscriptionResponse> => {
    const response = await apiFetch("/users/me/subscription/manage", {
      method: "POST",
    });
    if (!response.ok) {
      throw new ApiError(await parseErrorDetail(response), response.status);
    }
    return response.json() as Promise<ManageSubscriptionResponse>;
  };

export const redeemInviteCode = async (
  code: string,
): Promise<RedeemCodeResponse> => {
  const response = await apiFetch("/billing/redeem", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ code }),
  });
  if (!response.ok) {
    throw new ApiError(await parseErrorDetail(response), response.status);
  }
  return response.json() as Promise<RedeemCodeResponse>;
};

export const listInviteCodes = async (): Promise<InviteCode[]> => {
  const response = await apiFetch("/billing/invite-codes");
  if (!response.ok) {
    throw new ApiError(await parseErrorDetail(response), response.status);
  }
  const data = (await response.json()) as { codes: InviteCode[] };
  return data.codes;
};

export const createInviteCode = async (
  body: {
    plan_id?: string;
    max_uses?: number | null;
    expires_at?: string;
    custom_code?: string;
  },
): Promise<CreateInviteCodeResponse> => {
  const response = await apiFetch("/billing/invite-codes", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!response.ok) {
    throw new ApiError(await parseErrorDetail(response), response.status);
  }
  return response.json() as Promise<CreateInviteCodeResponse>;
};

export const updateInviteCodeExpiresAt = async (
  code: string,
  body: { expires_at: string | null },
): Promise<InviteCode> => {
  const response = await apiFetch(
    `/billing/invite-codes/${encodeURIComponent(code)}`,
    {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(body),
    },
  );
  if (!response.ok) {
    throw new ApiError(await parseErrorDetail(response), response.status);
  }
  return response.json() as Promise<InviteCode>;
};

export const listRedemptions = async (
  code: string,
): Promise<InviteRedemption[]> => {
  const response = await apiFetch(
    `/billing/invite-codes/${encodeURIComponent(code)}/redemptions`,
  );
  if (!response.ok) {
    throw new ApiError(await parseErrorDetail(response), response.status);
  }
  const data = (await response.json()) as { redemptions: InviteRedemption[] };
  return data.redemptions;
};